/**
 * Cross-browser copy-to-clipboard with secure-context fallback. Returns
 * `true` on success, `false` on any failure (clipboard API unavailable,
 * permission denied, browser blocked the action).
 *
 * Prefers the modern `navigator.clipboard.writeText`; falls back to the
 * legacy textarea + execCommand path for older browsers / insecure contexts
 * (e.g. http://localhost on Safari).
 *
 * Created for the API token mint modal where the plaintext PAT is shown
 * ONCE; the modal disables its close button until the user has clicked
 * copy. Callers are encouraged to surface a toast on success/failure rather
 * than letting silent failures happen.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text) return false;
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy path
  }
  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  if (typeof document === "undefined") return false;
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "-9999px";
  ta.style.left = "-9999px";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  try {
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    return ok;
  } catch {
    return false;
  } finally {
    document.body.removeChild(ta);
  }
}
